import { decimalsFor, minorUnitsPerMajor } from '@zuruflex/types';
/**
 * Money maths, always in minor units and always aware of the currency's
 * exponent.
 *
 * The trap this exists to avoid: dividing by 100. RWF has no minor unit, so
 * `45000` RWF is forty-five thousand francs, while `45000` GHS is four
 * hundred and fifty cedis. A hard-coded 100 silently prices a Kigali car at
 * one hundredth of its value.
 */
/** `12550, 'GHS'` → `"GH₵125.50"`; `45000, 'RWF'` → `"RWF 45,000"`. */
export function formatMoney(amount, currency, locale = 'en-GH') {
    const decimals = decimalsFor(currency);
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        // Pinning both to the currency's own exponent stops Intl rendering
        // "RWF 45,000.00" for a currency that has no cents.
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(amount / minorUnitsPerMajor(currency));
}
/** `125.5, 'GHS'` → `12550`. `45000, 'RWF'` → `45000`. */
export function toMinorUnits(major, currency) {
    return Math.round(major * minorUnitsPerMajor(currency));
}
export function toMajorUnits(minor, currency) {
    return minor / minorUnitsPerMajor(currency);
}
export function sumMinor(amounts) {
    return amounts.reduce((total, amount) => total + amount, 0);
}
/**
 * Percentage of a minor-unit amount, rounded half-up.
 * `applyRate(10000, 0.125)` → `1250`.
 *
 * Currency-independent: a percentage of an integer is an integer either way.
 */
export function applyRate(amount, rate) {
    return Math.round(amount * rate);
}
/**
 * Converts between currencies for **display only**.
 *
 * Crosses the exponent gap as well as the rate: RWF→GHS must go
 * 45000 francs → 390.15 cedis → 39015 pesewas, not 45000 → 45000×rate.
 */
export function convertMinor(amount, from, to, rate) {
    const major = toMajorUnits(amount, from);
    return toMinorUnits(major * rate, to);
}
